import React, { useState, useEffect, useRef } from "react";
import { colors } from '../../../../theme/colors';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  CustomSelect,
  CustomSelectField,
} from "../../../../components/common-components/CustomSelect";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../../../utils/axiosInstance";
import { JobEndPoint, CompanyEndPoint } from "../../../../api-routes";
import { useAllUserList } from "../../../../store/query/userQuery";
import useGlobalContext from "../../../../contexts-hooks/hooks/useGlobalContext";
import { CreateJobWrapper } from "./style";
import CreateJobDescription from "../../create-job-description";

const statusOptions = ["Active", "Paused", "Closed", "Cancelled"];
const priorityOptions = ["Medium", "High", "Low"];
const employmentTypeOptions = [
  "Full-Time",
  "Part-time",
  "Contract base",
  "Freelance",
];
const workModeOptions = ["Hybrid", "Remote", "On-site"];

const quillModules = {
  toolbar: [
    ["bold", "italic"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
};

const CreateJob = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useGlobalContext();
  const isAdmin = state?.user?.role === "admin";
  const isEditMode = Boolean(id);
  const isReadOnly = isEditMode && !isAdmin;

  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState("");
  const [existingJdFileName, setExistingJdFileName] = useState("");
  const [companyList, setCompanyList] = useState([]);

  const { data: usersData } = useAllUserList({
    page: 1,
    limit: 100,
    search: "",
  });
  const recruiterList = usersData?.data || [];

  const [jdFile, setJdFile] = useState(null);
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [showJdModal, setShowJdModal] = useState(false);

  const [formData, setFormData] = useState({
    roleDesignation: "",
    company: "",
    noOfPositions: 1,
    status: "Active",
    priority: "Medium",
    deadline: "",
    employmentType: "Full-Time",
    workMode: "Hybrid",
    budget: "",
    salesRep: "",
    accountManager: "",
    description: "",
  });
  const [initialFormData, setInitialFormData] = useState(null);

  const hasChanges =
    !isEditMode ||
    !initialFormData ||
    jdFile !== null ||
    Object.keys(formData).some(
      (key) => String(formData[key]) !== String(initialFormData[key]),
    );

  useEffect(() => {
    if (isEditMode) {
      const fetchJob = async () => {
        try {
          const res = await axiosInstance.get(
            `${JobEndPoint.GET_ALL_JOBS}/${id}`,
          );
          const job = res.data.data;
          setJobId(job.jobId);
          setExistingJdFileName(job.jdFileName || "");
          const loadedData = {
            roleDesignation: job.roleDesignation || "",
            company: job.company || "",
            noOfPositions: job.noOfPositions || 1,
            status: job.status || "Active",
            priority: job.priority || "Medium",
            deadline: job.deadline ? job.deadline.split("T")[0] : "",
            employmentType: job.employmentType || "Full-Time",
            workMode: job.workMode || "Hybrid",
            budget: job.budget || "",
            salesRep: job.salesRep || "",
            accountManager: job.accountManager || "",
            description: job.description || "",
          };
          setFormData(loadedData);
          setInitialFormData(loadedData);
        } catch (err) {
          console.error("Failed to fetch job", err);
        }
      };
      fetchJob();
    } else {
      const fetchNextJobId = async () => {
        try {
          const res = await axiosInstance.get(JobEndPoint.GET_NEXT_JOB_ID);
          setJobId(res.data.data.jobId);
        } catch (err) {
          console.error("Failed to fetch next Job ID", err);
        }
      };
      fetchNextJobId();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axiosInstance.get(CompanyEndPoint.GET_ALL_COMPANIES);
        setCompanyList(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch companies", err);
      }
    };
    fetchCompanies();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJdFile(file);
    }
  };

  const handleRemoveFile = () => {
    setJdFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreviewJd = async () => {
    if (jdFile) {
      const url = URL.createObjectURL(jdFile);
      window.open(url, "_blank");
      return;
    }
    if (isEditMode && existingJdFileName) {
      try {
        const res = await axiosInstance.get(
          `${JobEndPoint.GET_ALL_JOBS}/${id}/jd-file`,
          { responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank");
      } catch (err) {
        console.error("Failed to preview JD", err);
      }
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!isEditMode && !jdFile) {
      newErrors.jdFile = "JD upload is required";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    if (!formData.roleDesignation || !formData.company || !hasChanges) return;
    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });
      if (jdFile) {
        payload.append("jdFile", jdFile);
      }

      if (isEditMode) {
        await axiosInstance.put(`${JobEndPoint.GET_ALL_JOBS}/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.post(JobEndPoint.CREATE_JOB, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      navigate("/app/jobs");
    } catch (err) {
      console.error("Failed to save job", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateJobWrapper>
      <Box className="page-header">
        <Typography className="page-title">
          {isEditMode
            ? isReadOnly
              ? "View Job"
              : "Edit Job"
            : "Create New Job"}
        </Typography>
        <Typography className="page-subtitle">
          {isEditMode
            ? isReadOnly
              ? "View the details of this job requisition."
              : "Update the details of this job requisition."
            : "Fill in the details below to open a new requisition."}
        </Typography>
      </Box>

      <Box className="form-card">
        <Box className="section-block">
          <Typography className="section-title">Job Details</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Role/Designation"
                name="roleDesignation"
                value={formData.roleDesignation}
                onChange={handleChange}
                placeholder="e.g. Senior Frontend Engineer"
                variant="outlined"
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Job ID"
                value={jobId}
                placeholder="Auto-generated"
                variant="outlined"
                className="job-id-field"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomSelect
                fullWidth
                disabled={isReadOnly}
                options={companyList}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={
                  companyList.find((c) => c.name === formData.company) || null
                }
                onChange={(event, value) => {
                  setFormData((prev) => ({
                    ...prev,
                    company: value ? value.name : "",
                  }));
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    overflow: "auto",
                  },
                }}
                renderInput={(params) => (
                  <CustomSelectField
                    {...params}
                    label="Company/Clients"
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: `${colors.primary} !important`,
                        color: "#fff",
                      },
                    }}
                  >
                    {option.name || ""}
                  </MenuItem>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="No. of positions"
                name="noOfPositions"
                type="number"
                value={formData.noOfPositions}
                onChange={handleChange}
                variant="outlined"
                inputProps={{ min: 1 }}
                disabled={isReadOnly}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="section-block">
          <Typography className="section-title">Logistics & Status</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                variant="outlined"
                select
                disabled={isReadOnly}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                variant="outlined"
                select
                disabled={isReadOnly}
              >
                {priorityOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Deadline"
                name="deadline"
                type="date"
                value={formData.deadline}
                onChange={handleChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employment Type"
                name="employmentType"
                value={formData.employmentType}
                onChange={handleChange}
                variant="outlined"
                select
                disabled={isReadOnly}
              >
                {employmentTypeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Work Mode"
                name="workMode"
                value={formData.workMode}
                onChange={handleChange}
                variant="outlined"
                select
                disabled={isReadOnly}
              >
                {workModeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        <Box className="section-block">
          <Typography className="section-title">
            Financials & Assignment
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Budget (Annual Salary)"
                name="budget"
                type="number"
                value={formData.budget}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
                variant="outlined"
                placeholder="₹ Enter amount in LPA"
                className="budget-field"
                disabled={isReadOnly}
                helperText={
                  formData.budget ? (
                    <span
                      style={{
                        color: "#d32f2f",
                        fontStyle: "italic",
                        fontSize: "12px",
                      }}
                    >
                      💡 Budget should be annual (LPA).
                    </span>
                  ) : (
                    ""
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sales Rep"
                name="salesRep"
                value={formData.salesRep}
                onChange={handleChange}
                variant="outlined"
                placeholder="Enter name"
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomSelect
                fullWidth
                disabled={isReadOnly}
                options={recruiterList}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={
                  recruiterList.find(
                    (user) => user._id === formData.accountManager,
                  ) || null
                }
                onChange={(event, value) => {
                  setFormData((prev) => ({
                    ...prev,
                    accountManager: value ? value._id : "",
                  }));
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    overflow: "auto",
                  },
                }}
                renderInput={(params) => (
                  <CustomSelectField
                    {...params}
                    label="Assign Account Manager"
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: `${colors.primary} !important`,
                        color: "#fff",
                      },
                    }}
                  >
                    {option.name || ""}
                  </MenuItem>
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="section-block">
          <Typography className="section-title">Description</Typography>
          <Typography className="editor-label">
            Internal Notes / Job Description
          </Typography>
          <Box className="quill-container">
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              modules={quillModules}
              placeholder="Enter detailed job description and internal notes here..."
              readOnly={isReadOnly}
            />
          </Box>
        </Box>

        {!isReadOnly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1.5,
              mb: 1,
              borderRadius: "10px",
              backgroundColor: colors.primaryLight,
              border: "1px dashed #fca5a5",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showJdModal}
                  onChange={(e) => setShowJdModal(e.target.checked)}
                  sx={{
                    color: colors.primary,
                    "&.Mui-checked": { color: colors.primary },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DescriptionIcon sx={{ fontSize: 20, color: colors.primary }} />
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, color: colors.primary }}
                  >
                    Do you want to create a Job Description?
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        <Dialog
          open={showJdModal}
          onClose={() => setShowJdModal(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              maxHeight: "90vh",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <IconButton onClick={() => setShowJdModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ pt: 0 }}>
            <CreateJobDescription />
          </DialogContent>
        </Dialog>

        <Box className="section-block">
          <Typography className="section-title">
            Upload JD{" "}
            {!isEditMode && <span style={{ color: "#d32f2f" }}>*</span>}
          </Typography>
          {isEditMode && existingJdFileName && !jdFile && (
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InsertDriveFileIcon sx={{ color: colors.primary }} />
                <Typography sx={{ fontSize: "14px", color: "#333" }}>
                  Current: {existingJdFileName}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={handlePreviewJd}
                sx={{
                  textTransform: "none",
                  color: colors.primary,
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                Preview JD
              </Button>
            </Box>
          )}
          {!isReadOnly && (
            <Box className="upload-area">
              {!jdFile ? (
                <Box
                  className="upload-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUploadIcon className="upload-icon" />
                  <Typography className="upload-text">
                    {isEditMode
                      ? "Upload new JD file (optional)"
                      : "Click to upload JD file (required)"}
                  </Typography>
                  <Typography className="upload-hint">
                    PDF, DOC, DOCX (Max 10MB)
                  </Typography>
                </Box>
              ) : (
                <Box className="uploaded-file">
                  <Box className="file-info">
                    <InsertDriveFileIcon className="file-icon" />
                    <Box>
                      <Typography className="file-name">
                        {jdFile.name}
                      </Typography>
                      <Typography className="file-size">
                        {(jdFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={handlePreviewJd}
                      title="Preview JD"
                    >
                      <VisibilityIcon
                        fontSize="small"
                        sx={{ color: colors.primary }}
                      />
                    </IconButton>
                    <IconButton size="small" onClick={handleRemoveFile}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleFileChange(e);
                  setErrors((prev) => ({ ...prev, jdFile: "" }));
                }}
              />
            </Box>
          )}
          {errors.jdFile && (
            <Typography
              sx={{ fontSize: "12px", color: "#d32f2f", mt: 1, ml: 1.5 }}
            >
              {errors.jdFile}
            </Typography>
          )}
          {isReadOnly && !existingJdFileName && (
            <Typography sx={{ fontSize: "13px", color: "#999" }}>
              No JD file uploaded for this job.
            </Typography>
          )}
        </Box>

        <Box className="form-actions">
          <Button
            variant="outlined"
            className="cancel-btn"
            onClick={() => navigate("/app/jobs")}
          >
            {isReadOnly ? "Back" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              className="save-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode ? (
                "Update"
              ) : (
                "Save Job"
              )}
            </Button>
          )}
        </Box>
      </Box>
    </CreateJobWrapper>
  );
};

export default CreateJob;
